import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_KEY = 'product-x-next-preferences';

/**
 * How many command-palette entries are remembered.
 *
 * Short on purpose: recents fill the palette's first, unfiltered view, and a list long
 * enough to scroll is one nobody reads.
 */
const RECENTS_KEPT = 5;

/** How tall the Tasks page's rows are (spec 114 US2). */
export type TaskListDensity = 'cozy' | 'compact';

/**
 * Device-level interface preferences.
 *
 * The distinction against route search params matters and is easy to get
 * wrong: state that belongs to a *particular address* — the open task, the
 * board's view mode, active filters — lives in search params so it can be
 * shared and restored from a link. What lives here is state that belongs to a
 * *person on a device* and would be meaningless in someone else's link.
 *
 * Nothing that originates from the service may be stored here. This holds
 * choices, not data.
 */
export interface RecentWorkspace {
  id: string;
  /** ISO instant, this device's clock. */
  openedAt: string;
}

export interface PreferencesState {
  /**
   * `null` means the reader has never chosen, so the shell follows the
   * viewport. Once they toggle, their choice is authoritative at any width.
   */
  sidebarCollapsed: boolean | null;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  /** Hands control back to the viewport. */
  clearSidebarPreference: () => void;

  /**
   * Whether the builder's step palette is collapsed to icons.
   *
   * Unlike the sidebar this has no viewport default — it is a working
   * preference about how much canvas someone wants, not a response to how much
   * room there is, and it would be wrong for a wide screen to keep re-expanding
   * a panel a user deliberately folded away.
   */
  paletteCollapsed: boolean;
  togglePalette: () => void;

  /**
   * Whether the builder's inspector is folded to a rail.
   *
   * The same kind of preference as {@link paletteCollapsed}, on the other side
   * of the canvas: arranging a graph wants width, and editing one wants the
   * fields. Nobody is doing both in the same second, so both rails fold.
   *
   * Settable as well as toggleable, because a refused publish has to be able to
   * put the panel back — the reasons are listed inside it, and a folded panel
   * would hide the answer to the question the user just asked.
   */
  inspectorCollapsed: boolean;
  toggleInspector: () => void;
  setInspectorCollapsed: (collapsed: boolean) => void;

  /**
   * Whether the sidebar's workflow list is unfolded.
   *
   * Open by default: the workflows are the reason most people are in a workspace.
   * It folds because that list is the only part of the navigation that grows
   * with the workspace, and someone working in one workflow should be able to
   * put the other twenty away.
   *
   * Stays in this store rather than moving to the address, unlike the filters and
   * selections that share the sidebar's surfaces: nobody sends someone a link to a
   * folded sidebar (constitution §4).
   *
   * Renamed from `boardsExpanded` when the Boards and Workflows sections merged
   * (spec 094). This store is persisted, so anyone who had folded the old list
   * away finds the new one open once. Deliberately not migrated: a migration for a
   * sidebar fold costs more to keep correct than the single re-fold it saves.
   */
  workflowsExpanded: boolean;
  toggleWorkflows: () => void;

  /**
   * The command palette's most recent entries, most recent first.
   *
   * Named for what it holds rather than "palette", because `paletteCollapsed` above
   * already means the *builder's* step palette and two unrelated things called palette
   * in one interface is how a wrong field gets read.
   *
   * A device preference in the same sense the sidebar's collapse is: it is about this
   * person on this machine, and it would be meaningless in someone else's link. Ids
   * only — never labels, and never anything that came from the service (spec 077).
   */
  recentEntryIds: string[];
  rememberEntry: (id: string) => void;
  clearRecentEntries: () => void;

  /**
   * The workspaces this person opened last on this device, most recent first
   * (spec 122 follow-up: the front door's "pick up where you left off" row).
   *
   * Ids and the moment they were opened — both this device's own facts, never
   * anything that came from the service. The list the page shows is these ids
   * joined to the workspace list it has just read; an id the list no longer
   * holds is simply not shown.
   */
  recentWorkspaces: RecentWorkspace[];
  rememberWorkspace: (id: string) => void;

  /**
   * Workflows this person opened last in each workspace, by key, most recent
   * first (spec 128 R3) — what keeps a capped sidebar list from burying the
   * one in daily use, the objection spec 094 raised against a cap.
   */
  recentWorkflowKeys: Record<string, string[]>;
  rememberWorkflow: (workspaceId: string, workflowKey: string) => void;

  /** Workspaces whose setup checklist this person dismissed (spec 128 R5). */
  dismissedSetup: string[];
  dismissSetup: (workspaceId: string) => void;

  /**
   * Workflows this person keeps at the top of the sidebar's list.
   *
   * A device preference in the same sense `recentEntryIds` and the sidebar's own
   * collapse are: it is about this person on this machine, and it would be
   * meaningless in someone else's link. Ids only — never labels, and never
   * anything that came from the service.
   *
   * **Pins reorder; they never filter.** The list under the Workflows row shows
   * every workflow either way, so pinning is pure gain — it moves a door to the
   * top and removes none. That is what makes it safe to have no cap: the person
   * who has pinned nothing sees exactly what they saw before.
   *
   * Ids outlive the things they name, so a pin whose workflow has been deleted is
   * resolved away at read time rather than cleaned up on a schedule — the same
   * treatment `resolveRecents` gives the palette's remembered entries.
   */
  pinnedWorkflowIds: string[];
  togglePinnedWorkflow: (workflowId: string) => void;

  /**
   * Whether the task drawer opens at its wide width (spec 096 FR-012, FR-013).
   *
   * A device preference in the same sense the sidebar's collapse is: it is about
   * this person on this machine, and it would be meaningless in someone else's
   * link. Two widths and no free resize, so the choice is one boolean.
   */
  taskDrawerWide: boolean;
  toggleTaskDrawerWide: () => void;
  /**
   * Which step columns are folded to a rail, per workflow key (spec 098
   * FR-017). A device preference like the rest: what this person wants out of
   * the way on this machine, never part of a link. Keyed by step id, so a
   * renamed step keeps its fold and a removed one is simply ignored.
   */
  collapsedBoardColumns: Record<string, string[]>;
  toggleBoardColumn: (workflowKey: string, stepId: string) => void;
  /**
   * How tall the Tasks page's rows are — two lines or one (spec 114 FR-009).
   * A device preference like the rest: how this person likes to read on this
   * machine. Kept out of the address on purpose, so a shared link never forces
   * one reader's density on another.
   */
  taskListDensity: TaskListDensity;
  setTaskListDensity: (density: TaskListDensity) => void;
  /**
   * Whether the run page's Activity shows step and run events beside the
   * subtask changes (spec 148). Off leaves only the changes.
   */
  showRunProgressEvents: boolean;
  setShowRunProgressEvents: (show: boolean) => void;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: null,
      setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed });
      },
      toggleSidebar: () => {
        set({ sidebarCollapsed: !(get().sidebarCollapsed ?? false) });
      },
      clearSidebarPreference: () => {
        set({ sidebarCollapsed: null });
      },
      paletteCollapsed: false,
      togglePalette: () => {
        set({ paletteCollapsed: !get().paletteCollapsed });
      },
      inspectorCollapsed: false,
      toggleInspector: () => {
        set({ inspectorCollapsed: !get().inspectorCollapsed });
      },
      setInspectorCollapsed: (collapsed) => {
        set({ inspectorCollapsed: collapsed });
      },
      workflowsExpanded: true,
      toggleWorkflows: () => {
        set({ workflowsExpanded: !get().workflowsExpanded });
      },
      taskDrawerWide: false,
      toggleTaskDrawerWide: () => {
        set({ taskDrawerWide: !get().taskDrawerWide });
      },
      taskListDensity: 'cozy',
      setTaskListDensity: (density) => {
        set({ taskListDensity: density });
      },
      showRunProgressEvents: true,
      setShowRunProgressEvents: (show) => {
        set({ showRunProgressEvents: show });
      },
      collapsedBoardColumns: {},
      toggleBoardColumn: (workflowKey, stepId) => {
        const all = get().collapsedBoardColumns;
        const current = all[workflowKey] ?? [];
        const next = current.includes(stepId)
          ? current.filter((id) => id !== stepId)
          : [...current, stepId];
        set({ collapsedBoardColumns: { ...all, [workflowKey]: next } });
      },
      pinnedWorkflowIds: [],
      togglePinnedWorkflow: (workflowId) => {
        const pinned = get().pinnedWorkflowIds;
        set({
          // Appended rather than prepended: pins keep the order they were made
          // in, so a list someone has arranged does not rearrange itself the next
          // time they add one.
          pinnedWorkflowIds: pinned.includes(workflowId)
            ? pinned.filter((id) => id !== workflowId)
            : [...pinned, workflowId],
        });
      },
      recentWorkflowKeys: {},
      rememberWorkflow: (workspaceId, workflowKey) => {
        const current = get().recentWorkflowKeys[workspaceId] ?? [];
        set({
          recentWorkflowKeys: {
            ...get().recentWorkflowKeys,
            [workspaceId]: [
              workflowKey,
              ...current.filter((key) => key !== workflowKey),
            ].slice(0, 10),
          },
        });
      },
      dismissedSetup: [],
      dismissSetup: (workspaceId) => {
        const dismissed = get().dismissedSetup;
        if (!dismissed.includes(workspaceId)) {
          set({ dismissedSetup: [...dismissed, workspaceId] });
        }
      },
      recentWorkspaces: [],
      rememberWorkspace: (id) => {
        set({
          recentWorkspaces: [
            { id, openedAt: new Date().toISOString() },
            ...get().recentWorkspaces.filter((kept) => kept.id !== id),
          ].slice(0, RECENTS_KEPT),
        });
      },
      recentEntryIds: [],
      rememberEntry: (id) => {
        // Deduplicated and most-recent-first, so running the same thing twice does not
        // fill the list with it. Kept here rather than imported from the palette: this
        // module is shared, and a shared store reaching into a feature inverts the
        // dependency the package boundaries exist to enforce.
        set({
          recentEntryIds: [
            id,
            ...get().recentEntryIds.filter((kept) => kept !== id),
          ].slice(0, RECENTS_KEPT),
        });
      },
      clearRecentEntries: () => {
        set({ recentEntryIds: [] });
      },
    }),
    { name: STORAGE_KEY },
  ),
);

/**
 * Resolves the effective collapse state.
 *
 * An explicit choice wins at any width. Without one, a narrow viewport
 * collapses the sidebar so the content area stays usable.
 */
export function resolveSidebarCollapsed(
  preference: boolean | null,
  viewportIsNarrow: boolean,
): boolean {
  return preference ?? viewportIsNarrow;
}
